import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { IdeozLoading, NotFoundState } from '@ideoz/ui';
import { useStartups, useDiscussionRoom, useDiscussionConversations } from '@ideoz/core';

/**
 * Discussion Room entry resolver — `/:startupSlug/discussions`.
 *
 * Mirrors ScopeConversationResolver: decides which conversation to open before
 * the chat workspace mounts, then redirects (replace).
 *   - Room has conversations → open the most-recent one.
 *   - Empty room → `…/discussions/new`. The first message creates the
 *     conversation on the AI API; Nest does not create room conversations.
 */
export default function DiscussionResolver(): JSX.Element {
  const { startupSlug } = useParams<{ startupSlug: string }>();
  const navigate = useNavigate();

  const {
    data: startups = [],
    isFetched: startupsFetched,
    isFetching: startupsFetching,
    isError: startupsError,
  } = useStartups();
  const matchedStartup = startups.find((s) => s.slug === startupSlug) ?? null;
  const startupId = matchedStartup?.id ?? null;

  const { data: room } = useDiscussionRoom(startupId);
  const { data: convData, isSuccess: conversationsLoaded } = useDiscussionConversations(
    startupId,
    room?.id
  );

  useEffect(() => {
    if (!startupSlug || !startupId || !room || !conversationsLoaded) return;
    const convs = convData ?? [];
    const target = convs.length > 0 ? convs[0].id : 'new';
    navigate(`/${startupSlug}/discussions/${target}`, { replace: true });
  }, [startupSlug, startupId, room, conversationsLoaded, convData, navigate]);

  // 404 only once the startups query has settled (same guard as the workspace).
  if (startupsFetched && !startupsFetching && !startupsError && (!matchedStartup || !startupId)) {
    return (
      <NotFoundState
        description="Startup not found"
        actionLabel="Back to My Startups"
        onAction={() => navigate('/startups')}
      />
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center">
      <IdeozLoading size="lg" />
    </div>
  );
}
